import clsx from "clsx";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonOpenMenu } from "./ButtonOpenMenu";

export const fontFamilyOptions = ["TimesRoman", "Helvetica", "Courier"];

const StyleTypographySection = ({ config, handleInputChange, disabled }) => {
  const { t } = useTranslation();

  // Options prédéfinies pour les tailles de police
  const titleFontSizes = [
    {
      value: 14,
      label: `14px - ${t("sections.style.verySmall")}`,
    },
    { value: 16, label: `16px - ${t("sections.style.small")}` },
    {
      value: 18,
      label: `18px - ${t("sections.style.standard")}`,
    },
    { value: 20, label: `20px - ${t("sections.style.large")}` },
    {
      value: 24,
      label: `24px - ${t("sections.style.veryLarge")}`,
    },
    { value: 28, label: `28px - ${t("sections.style.huge")}` },
    {
      value: 32,
      label: `32px - ${t("sections.style.maximum")}`,
    },
  ];

  const chapterFontSizes = [
    {
      value: 11,
      label: `11px - ${t("sections.style.minimum")}`,
    },
    {
      value: 12,
      label: `12px - ${t("sections.style.verySmall")}`,
    },
    {
      value: 13,
      label: `13px - ${t("sections.style.small")}`,
    },
    {
      value: 14,
      label: `14px - ${t("sections.style.medium")}`,
    },
    {
      value: 15,
      label: `15px - ${t("sections.style.standard")}`,
    },
    {
      value: 16,
      label: `16px - ${t("sections.style.large")}`,
    },
    {
      value: 18,
      label: `18px - ${t("sections.style.veryLarge")}`,
    },
    {
      value: 20,
      label: `20px - ${t("sections.style.maximum")}`,
    },
  ];

  const ruleTitleFontSizes = [
    {
      value: 10,
      label: `10px - ${t("sections.style.compact")}`,
    },
    {
      value: 11,
      label: `11px - ${t("sections.style.verySmall")}`,
    },
    {
      value: 12,
      label: `12px - ${t("sections.style.small")}`,
    },
    {
      value: 13,
      label: `13px - ${t("sections.style.standard")}`,
    },
    {
      value: 14,
      label: `14px - ${t("sections.style.large")}`,
    },
    {
      value: 16,
      label: `16px - ${t("sections.style.veryLarge")}`,
    },
    {
      value: 18,
      label: `18px - ${t("sections.style.maximum")}`,
    },
  ];

  const textFontSizes = [
    { value: 8, label: `8px - ${t("sections.style.verySmall")}` },
    { value: 9, label: `9px - ${t("sections.style.small")}` },
    { value: 10, label: `10px - ${t("sections.style.compact")}` },
    {
      value: 11,
      label: `11px - ${t("sections.style.standard")}`,
    },
    {
      value: 12,
      label: `12px - ${t("sections.style.comfortable")}`,
    },
    { value: 13, label: `13px - ${t("sections.style.large")}` },
    {
      value: 14,
      label: `14px - ${t("sections.style.veryLarge")}`,
    },
    { value: 16, label: `16px - ${t("sections.style.maximum")}` },
  ];

  const lineSpacingOptions = [
    { value: 1.0, label: `1.0 - ${t("sections.style.veryTight")}` },
    { value: 1.05, label: `1.05 - ${t("sections.style.tight")} -` },
    { value: 1.1, label: `1.1 - ${t("sections.style.tight")}` },
    {
      value: 1.15,
      label: `1.15 - ${t("sections.style.standardMinus")}`,
    },
    { value: 1.2, label: `1.2 - ${t("sections.style.standard")}` },
    {
      value: 1.25,
      label: `1.25 - ${t("sections.style.standardPlus")}`,
    },
    { value: 1.3, label: `1.3 - ${t("sections.style.relaxed")}` },
    {
      value: 1.4,
      label: `1.4 - ${t("sections.style.comfortable")}`,
    },
    {
      value: 1.6,
      label: `1.6 - ${t("sections.style.veryRelaxed")}`,
    },
    {
      value: 2.0,
      label: `2.0 - ${t("sections.style.doubleInterline")}`,
    },
  ];

  const logoWidthOptions = [
    { value: 80, label: `80px - ${t("sections.style.minimum")}` },
    { value: 100, label: "100px" },
    { value: 120, label: `120px - ${t("sections.style.standard")}` },
    { value: 140, label: "140px" },
    { value: 160, label: "160px" },
    { value: 180, label: "180px" },
    { value: 200, label: "200px" },
    { value: 220, label: "220px" },
    { value: 240, label: "240px" },
    { value: 260, label: "260px" },
    { value: 280, label: "280px" },
    { value: 300, label: `300px  - ${t("sections.style.maximum")}` },
  ];

  const presets = [
    {
      name: "standard",
      mainTitleFontSize: 18,
      chapterFontSize: 14,
      ruleTitleFontSize: 13,
      ruleFontSize: 11,
      lineSpacing: 1.2,
      fontFamily: "Helvetica",
      logoWidth: 120,
    },
    {
      name: "compact",
      mainTitleFontSize: 16,
      chapterFontSize: 13,
      ruleTitleFontSize: 11,
      ruleFontSize: 9,
      lineSpacing: 1.1,
      fontFamily: "Helvetica",
      logoWidth: 100,
    },
    {
      name: "readable",
      mainTitleFontSize: 24,
      chapterFontSize: 18,
      ruleTitleFontSize: 16,
      ruleFontSize: 13,
      lineSpacing: 1.4,
      fontFamily: "Helvetica",
      logoWidth: 140,
    },
    {
      name: "accessibility",
      mainTitleFontSize: 24,
      chapterFontSize: 20,
      ruleTitleFontSize: 18,
      ruleFontSize: 14,
      lineSpacing: 1.5,
      fontFamily: "Helvetica",
      logoWidth: 160,
    },
  ];

  const [isOpen, setIsOpen] = useState(false);

  let selectedPreset = "none";
  for (const preset of presets) {
    if (
      config.mainTitleFontSize === preset.mainTitleFontSize &&
      config.chapterFontSize === preset.chapterFontSize &&
      config.ruleTitleFontSize === preset.ruleTitleFontSize &&
      config.ruleFontSize === preset.ruleFontSize &&
      config.lineSpacing === preset.lineSpacing &&
      config.logoWidth === preset.logoWidth
    ) {
      selectedPreset = preset.name;
      break;
    }
  }

  const findPreset = (preset) => {
    return presets.find((p) => p.name === preset);
  };

  const changePreset = (preset) => {
    const presetData = findPreset(preset);
    if (presetData) {
      handleInputChange("multipleFields", presetData);
    } else {
      console.error(`Preset ${preset} not found`);
    }
  };

  const standard = findPreset("standard");

  if (!config.logoWidth) {
    config.logoWidth = standard.logoWidth;
  }

  return (
    <div className="param-group">
      <div className="param-group-header">
        <h3 className="param-group-title">
          <span>🎨</span>
          {t("sections.style.title")}
        </h3>

        <ButtonOpenMenu isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>
      <div
        className={clsx("grid grid-cols-1 gap-4 md:grid-cols-2", {
          hidden: !isOpen,
        })}
      >
        <div className="form-group">
          <label className="form-label" htmlFor="logoWidth">
            📏 {t("sections.style.logoWidth")}
          </label>
          <select
            id="logoWidth"
            className={clsx("form-input", {
              "opacity-50 cursor-not-allowed": disabled,
              "hover:border-primary-400 focus:border-primary-500": !disabled,
            })}
            value={config.logoWidth}
            onChange={(e) => {
              const width = parseInt(e.target.value);
              handleInputChange("logoWidth", width);
            }}
            disabled={disabled}
          >
            {logoWidthOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="mainTitleFontSize">
            📏 {t("sections.style.mainTitleFontSize")}
          </label>
          <select
            id="mainTitleFontSize"
            className={clsx("form-input", {
              "opacity-50 cursor-not-allowed": disabled,
              "hover:border-primary-400 focus:border-primary-500": !disabled,
            })}
            value={config.mainTitleFontSize || standard.mainTitleFontSize}
            onChange={(e) =>
              handleInputChange("mainTitleFontSize", parseInt(e.target.value))
            }
            disabled={disabled}
          >
            {titleFontSizes.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="chapterFontSize">
            📝 {t("sections.style.chapterFontSize")}
          </label>
          <select
            id="chapterFontSize"
            className={clsx("form-input", {
              "opacity-50 cursor-not-allowed": disabled,
              "hover:border-primary-400 focus:border-primary-500": !disabled,
            })}
            value={config.chapterFontSize || standard.chapterFontSize}
            onChange={(e) =>
              handleInputChange("chapterFontSize", parseInt(e.target.value))
            }
            disabled={disabled}
          >
            {chapterFontSizes.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="ruleTitleFontSize">
            📝 {t("sections.style.ruleTitleFontSize")}
          </label>
          <select
            id="ruleTitleFontSize"
            className={clsx("form-input", {
              "opacity-50 cursor-not-allowed": disabled,
              "hover:border-primary-400 focus:border-primary-500": !disabled,
            })}
            value={config.ruleTitleFontSize || standard.ruleTitleFontSize}
            onChange={(e) =>
              handleInputChange("ruleTitleFontSize", parseInt(e.target.value))
            }
            disabled={disabled}
          >
            {ruleTitleFontSizes.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="ruleFontSize">
            📄 {t("sections.style.ruleFontSize")}
          </label>
          <select
            id="ruleFontSize"
            className={clsx("form-input", {
              "opacity-50 cursor-not-allowed": disabled,
              "hover:border-primary-400 focus:border-primary-500": !disabled,
            })}
            value={config.ruleFontSize || standard.ruleFontSize}
            onChange={(e) =>
              handleInputChange("ruleFontSize", parseInt(e.target.value))
            }
            disabled={disabled}
          >
            {textFontSizes.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="lineSpacing">
            📏 {t("sections.style.lineSpacing")}
          </label>
          <select
            id="lineSpacing"
            className={clsx("form-input", {
              "opacity-50 cursor-not-allowed": disabled,
              "hover:border-primary-400 focus:border-primary-500": !disabled,
            })}
            value={config.lineSpacing || standard.lineSpacing || 1.2}
            onChange={(e) =>
              handleInputChange("lineSpacing", parseFloat(e.target.value))
            }
            disabled={disabled}
          >
            {lineSpacingOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-span-2 form-group">
          <label className="form-label" htmlFor="fontFamily">
            🔤 {t("sections.style.fontFamily")}
          </label>
          <select
            id="fontFamily"
            className={clsx("form-input", {
              "opacity-50 cursor-not-allowed": disabled,
              "hover:border-primary-400 focus:border-primary-500": !disabled,
            })}
            value={config.fontFamily || standard.fontFamily}
            onChange={(e) => handleInputChange("fontFamily", e.target.value)}
            disabled={disabled}
          >
            {fontFamilyOptions.map((option) => (
              <option key={option} value={option}>
                {t(`pdfGenerator.fontFamily.${option}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Présets typographiques */}
      <div className="p-3 mt-4 rounded-lg border border-primary-200 bg-background-50">
        <div className="mb-2 text-sm font-medium text-text-700">
          ✨ {t("sections.style.presets")} : {selectedPreset}
        </div>
        <div className="flex flex-row flex-wrap gap-1 justify-between">
          <button
            type="button"
            className={clsx(
              "px-2 py-1 text-xs border transition-all duration-200 btn",
              selectedPreset === "compact"
                ? "border-accent-500 bg-accent-100 text-accent-800 shadow-lg ring-2 ring-accent-300"
                : "border-secondary-300 bg-secondary-100 text-secondary-700",
              {
                "opacity-50 cursor-not-allowed": disabled,
                "hover:bg-secondary-200 hover:scale-105 active:scale-95":
                  !disabled && selectedPreset !== "compact",
                "hover:bg-accent-200 hover:scale-105 active:scale-95":
                  !disabled && selectedPreset === "compact",
              }
            )}
            onClick={() => {
              changePreset("compact");
            }}
            disabled={disabled}
          >
            📄 Compact
          </button>
          <button
            type="button"
            className={clsx(
              "px-2 py-1 text-xs border transition-all duration-200 btn",
              selectedPreset === "standard"
                ? "border-accent-500 bg-accent-100 text-accent-800 shadow-lg ring-2 ring-accent-300"
                : "border-primary-300 bg-primary-100 text-primary-700",
              {
                "opacity-50 cursor-not-allowed": disabled,
                "hover:bg-primary-200 hover:scale-105 active:scale-95":
                  !disabled && selectedPreset !== "standard",
                "hover:bg-accent-200 hover:scale-105 active:scale-95":
                  !disabled && selectedPreset === "standard",
              }
            )}
            onClick={() => {
              changePreset("standard");
            }}
            disabled={disabled}
          >
            📋 {t("sections.style.presets.standard")}
          </button>
          <button
            type="button"
            className={clsx(
              "px-2 py-1 text-xs border transition-all duration-200 btn",
              selectedPreset === "readable"
                ? "border-accent-500 bg-accent-100 text-accent-800 shadow-lg ring-2 ring-accent-300"
                : "border-accent-300 bg-accent-100 text-accent-700",
              {
                "opacity-50 cursor-not-allowed": disabled,
                "hover:bg-accent-200 hover:scale-105 active:scale-95":
                  !disabled && selectedPreset === "readable",
              }
            )}
            onClick={() => {
              changePreset("readable");
            }}
            disabled={disabled}
          >
            🎨 {t("sections.style.presets.readable")}
          </button>
          <button
            type="button"
            className={clsx(
              "px-2 py-1 text-xs border transition-all duration-200 btn",
              selectedPreset === "accessibility"
                ? "border-accent-500 bg-accent-100 text-accent-800 shadow-lg ring-2 ring-accent-300"
                : "border-accent-400 bg-accent-200 text-accent-800",
              {
                "opacity-50 cursor-not-allowed": disabled,
                "hover:bg-accent-300 hover:scale-105 active:scale-95":
                  !disabled && selectedPreset !== "accessibility",
                "hover:bg-accent-200 hover:scale-105 active:scale-95":
                  !disabled && selectedPreset === "accessibility",
              }
            )}
            onClick={() => {
              changePreset("accessibility");
            }}
            disabled={disabled}
          >
            🔍 {t("sections.style.presets.accessibility")}
          </button>
        </div>
      </div>

      {/* Aperçu typographique */}
      <div
        className={clsx(
          "p-4 mt-4 rounded-lg border border-primary-300 bg-background-100",
          {
            hidden: !isOpen,
          }
        )}
      >
        <div className="mb-3 text-sm font-medium text-text-700">
          👁️ {t("sections.style.preview")} :
        </div>
        <div
          className="w-full rounded-sm border h-fit border-primary-300"
          style={{
            color: "#111111",
            backgroundColor: "#f0f0f0",
          }}
        >
          <div
            className="space-y-2"
            style={{
              color: "#111111",
              backgroundColor: "#f0f0f0",
              lineHeight: config.lineSpacing || 1.2,
              marginLeft: config.leftMargin || "0",
              marginRight: config.rightMargin || "0",
              marginTop: config.topMargin || "0",
              marginBottom: config.bottomMargin || "0",
            }}
          >
            <div
              style={{
                fontSize: `${config.mainTitleFontSize || 18}px`,
                fontWeight: "bold",
                fontFamily: config.fontFamily || standard.fontFamily,
              }}
            >
              {config.path}
              {config.mainTitle || "POKER TOURNAMENT DIRECTORS ASSN."}
            </div>
            <div
              style={{
                fontSize: `${config.chapterFontSize || 15}px`,
                fontWeight: "600",
                fontFamily: config.fontFamily || standard.fontFamily,
                lineHeight: config.lineSpacing || 1.2,
              }}
            >
              GENERAL CONCEPTS
            </div>
            <div
              style={{
                fontSize: `${config.ruleTitleFontSize || 13}px`,
                fontWeight: "600",
                fontFamily: config.fontFamily || standard.fontFamily,
                lineHeight: config.lineSpacing || 1.2,
              }}
            >
              3. Official Terminology and Gestures
            </div>
            <div
              style={{
                fontSize: `${config.ruleFontSize || 11}px`,
                fontFamily: config.fontFamily || standard.fontFamily,
                textAlign: config.textAlign || "left",
              }}
            >
              Official betting terms are simple, unmistakable, time-honored
              declarations like bet, raise, call, fold, check, all-in, complete,
              and pot (pot-limit only). Regional terms may also meet this test.
              (...)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StyleTypographySection;
